import React, { useEffect, useRef, useState } from 'react';

import PreviewPopout from './PreviewPopout';

const LIMIT = 4 * 1024 * 1024;
const ERRORS = {
  adb_preview_update_required: 'Update the Android relay app to enable ADB preview.',
  adb_preview_relay_unavailable: 'No eligible Android relay is available for this target.',
  adb_preview_busy: 'A capture or device maintenance task is already active. Try again shortly.',
  adb_target_not_authorized: 'Configure and authorize ADB for this target first.',
  adb_preview_expired: 'Capture expired. Press Capture now to try again.',
  adb_preview_invalid_png: 'The target returned an incomplete or unsupported image.',
  adb_preview_oversized: 'The target image exceeds the preview size limit.',
  adb_preview_capture_failed: 'The target could not capture its display.',
  adb_preview_scope_changed: 'The target setup changed. Check its connection and capture again.',
};

export default function AdbRemotePreview({ deviceId, name, disabled = false }) {
  const [capture, setCapture] = useState(null);
  const [image, setImage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('Capture the target screen when you need it.');
  const active = useRef(null);
  const imageRef = useRef(null);
  const alive = useRef(true);
  const previewWindowVisible = useRef(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const base = `/api/site-control/targets/${deviceId}/preview`;
  const clearImage = () => {
    if (imageRef.current) URL.revokeObjectURL(imageRef.current);
    imageRef.current = null;
    if (alive.current) { setImage(null); setCapture(null); }
  };
  const stop = () => {
    const job = active.current;
    active.current = null;
    if (!job) return;
    job.controller.abort(); clearTimeout(job.timer); clearTimeout(job.deadline);
    job.wake?.();
    if (!job.consumed) fetch(`${base}/${job.id}`, { method: 'DELETE', headers, keepalive: true }).catch(() => {});
  };
  useEffect(() => {
    alive.current = true;
    const onVisibility = () => { if (document.visibilityState === 'hidden' && !previewWindowVisible.current) { stop(); clearImage(); setBusy(false); setMessage('Capture cancelled while this page was hidden.'); } };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { alive.current = false; document.removeEventListener('visibilitychange', onVisibility); stop(); clearImage(); };
    // Device/provider changes remount this component and destroy its capture scope.
  }, []);

  useEffect(() => {
    if (disabled) { stop(); clearImage(); setBusy(false); }
  }, [disabled]);

  const start = async () => {
    if (active.current || disabled) return;
    let id;
    try {
      const bytes = window.crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
      const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
      id = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    } catch { setMessage('Could not create a capture request in this browser.'); return; }
    clearImage(); setBusy(true); setMessage('Capturing target screen…');
    const job = { id, controller: new AbortController() };
    active.current = job;
    const current = () => alive.current && active.current === job;
    job.deadline = setTimeout(() => {
      if (!current()) return;
      stop(); clearImage(); setBusy(false); setMessage('Capture expired. Press Capture now to try again.');
    }, 35000);
    const request = async (suffix = '', options = {}) => {
      const response = await fetch(`${base}/${id}${suffix}`, { ...options, headers, signal: job.controller.signal });
      if (!response.ok) {
        let code; try { code = (await response.json()).status; } catch {}
        throw new Error(ERRORS[code] || 'Could not capture this target. Check ADB connectivity and try again.');
      }
      return response;
    };
    try {
      let result = await (await request('', { method: 'POST' })).json();
      while (current() && ['capturing', 'validating'].includes(result.capture?.status)) {
        await new Promise((resolve) => { job.wake = resolve; job.timer = setTimeout(resolve, 500); });
        if (!current()) return;
        result = await (await request()).json();
      }
      if (!current()) return;
      const details = result.capture;
      if (details?.status !== 'ready') throw new Error(ERRORS[details?.error] || 'The target did not produce a preview. Try Capture now again.');
      if (Number(details.targetDeviceId) !== Number(deviceId) || details.id !== id || details.method !== 'adb_remote') throw new Error('The capture did not match this target.');
      const response = await request('/image');
      const bytes = await response.blob();
      if (!current()) return;
      job.consumed = true;
      if (!bytes.size || bytes.size > LIMIT || bytes.type !== 'image/png') throw new Error('The target returned an unsupported image.');
      const url = URL.createObjectURL(bytes);
      imageRef.current = url; setImage(url); setCapture(details);
      setMessage('Captured via ADB Remote. Protected content may appear black.');
    } catch (error) {
      if (current()) { clearImage(); setMessage(error.message || 'Could not capture the target screen.'); }
    } finally {
      if (current()) { stop(); setBusy(false); }
    }
  };

  return <>
    <div className="dashboard-tv-remote-panel-header"><h4>ADB Preview</h4>
      <div className="dashboard-tv-remote-actions">
        <button type="button" className="secondary" onClick={start} disabled={disabled || busy}>Capture now</button>
        <PreviewPopout name={name} image={image} caption={message}
          onRefresh={start} refreshDisabled={disabled || busy}
          onVisibilityChange={(visible) => { previewWindowVisible.current = visible; }} />
        {busy && <button type="button" className="secondary" onClick={() => { stop(); clearImage(); setBusy(false); setMessage('Capture cancelled.'); }}>Cancel</button>}
        {image && <button type="button" className="secondary" onClick={() => { clearImage(); setMessage('Preview cleared.'); }}>Clear</button>}
      </div>
    </div>
    <div className="dashboard-tv-remote-preview-frame">
      {image ? <img src={image} alt={`ADB capture of ${name}`} className="dashboard-tv-remote-preview-image"
        onError={() => { clearImage(); setMessage('The target image could not be displayed.'); }} />
        : <div className="dashboard-tv-remote-preview-empty"><strong>{busy ? 'Capturing…' : 'Your TV preview'}</strong><span>Press Capture now to take a picture.</span></div>}
    </div>
    <div className="dashboard-tv-remote-preview-caption" role="status">
      {capture && <span>{name} · ADB Remote · {new Date(capture.capturedAt).toLocaleString()} · Relay {capture.relayDeviceId}<br /></span>}
      {message}
    </div>
  </>;
}
